import type { Atom } from 'jotai';
import { memo } from 'react';
import { FreeFirstNoFreeResults } from '@/components/FreeFirstNoFreeResults';
import { NoResults } from '@/components/NoResults';
import { ShowMoreAssetsHome } from '@/components/ShowMoreAssetsHome';
import { VirtualizedTrackList } from '@/components/VirtualizedTrackList';
import type { QueueState, SearchDocument } from '@/features/AudioPlayer';
import { Button, Heading, Loading } from '@music-vine/cadence/ui';
import { toLoggableAssetType, trackSectionShowMore } from '@/utils/analytics/showFreeFirst';
import { getSectionCopy, type SectionAssetType } from '@/utils/show-free-first/sectionCopy';
import { getPageType } from '@/utils/tracking';
import { useRouter } from 'next/router';

// Shared SSR sizing estimate for both lists (matches the single-list layout).
const INITIAL_RECT = {
  width: 800,
  height: 600
} as const;
export interface TrackSection {
  hasMore: boolean;
  isError: boolean;
  isFetchingMore: boolean;
  isLoading: boolean;
  onShowMore: () => void;
  // Pages loaded for this section — the `pageIndex` analytics dimension.
  pageIndex?: number;
  pageQueueAtom: Atom<QueueState | null>;
  results: SearchDocument[];
}
const ShowMoreControl = ({
  assetType,
  fullWidth,
  isFetchingMore,
  label,
  onShowMore,
  pageIndex,
  tier
}: {
  assetType: SectionAssetType;
  fullWidth: boolean;
  isFetchingMore: boolean;
  label: string;
  onShowMore: () => void;
  pageIndex: number;
  tier: 'premium' | 'free';
}) => {
  const {
    asPath
  } = useRouter();
  const handleShowMore = () => {
    onShowMore();
    trackSectionShowMore(tier, {
      asPath,
      assetType: toLoggableAssetType(assetType),
      pageType: getPageType(asPath)?.contentType,
      pageIndex
    });
  };
  if (fullWidth) {
    return <ShowMoreAssetsHome className="mt-4" isFetchingNextPage={isFetchingMore} label={label} onClick={handleShowMore} />;
  }
  return <div className="mt-4 flex justify-center" data-sentry-component="ShowMoreControl" data-sentry-source-file="FreeFirstTrackSections.tsx">
      <Button className="px-3" disabled={isFetchingMore} onClick={handleShowMore} size="sm" variant="light" data-sentry-element="Button" data-sentry-source-file="FreeFirstTrackSections.tsx">
        <Loading visible={isFetchingMore} data-sentry-element="Loading" data-sentry-source-file="FreeFirstTrackSections.tsx" /> {label}
      </Button>
    </div>;
};
const Section = ({
  assetType,
  section,
  title,
  fullWidthShowMore,
  tier
}: {
  assetType: SectionAssetType;
  section: TrackSection;
  title?: string;
  fullWidthShowMore: boolean;
  tier: 'premium' | 'free';
}) =>
// Discrete paging: "Show more" loads one page per click, no infinite scroll.
<div data-sentry-component="Section" data-sentry-source-file="FreeFirstTrackSections.tsx">
    {title ? <Heading className="mb-2 font-aeonik @md:text-2xl text-2xl" fontFamily={null} variant="h5">
        {title}
      </Heading> : null}
    <VirtualizedTrackList initialRect={INITIAL_RECT} isError={section.isError} isFetchingNextPage={section.isFetchingMore} isLoading={section.isLoading} pageQueueAtom={section.pageQueueAtom} results={section.results} data-sentry-element="VirtualizedTrackList" data-sentry-source-file="FreeFirstTrackSections.tsx" />
    {section.hasMore && !section.isFetchingMore ? <ShowMoreControl assetType={assetType} fullWidth={fullWidthShowMore} isFetchingMore={section.isFetchingMore} label={tier === 'free' ? 'Show more free' : 'Show more premium'} onShowMore={section.onShowMore} pageIndex={section.pageIndex ?? 0} tier={tier} /> : null}
  </div>;
interface FreeFirstTrackSectionsProps {
  assetType: SectionAssetType;
  free: TrackSection;
  premium: TrackSection;
  premiumTitle?: string;
}

// Shown while loading (skeletons) or when it has hits; a zero-hit section
// collapses entirely.
const isSectionVisible = (section: TrackSection) => section.isLoading || section.results.length > 0;
const isSectionResolvedEmpty = (section: TrackSection) => !(section.isLoading || section.isError) && section.results.length === 0;
export const FreeFirstTrackSections = memo(({
  assetType,
  free,
  premium,
  premiumTitle
}: FreeFirstTrackSectionsProps) => {
  const resolvedPremiumTitle = premiumTitle ?? getSectionCopy({
    assetType,
    pageType: 'other'
  }).premiumHeading;
  const showNoResults = isSectionResolvedEmpty(free) && isSectionResolvedEmpty(premium);
  if (showNoResults) {
    return <NoResults />;
  }

  // Premium matches but no free tracks: surface an empty state above the
  // premium picks rather than silently dropping into a premium-only list.
  const showFreeEmptyState = isSectionResolvedEmpty(free);
  return <div className="flex flex-col gap-12">
        {isSectionVisible(free) ? <Section assetType={assetType} fullWidthShowMore={true} section={free} tier={'free'} /> : null}
        {showFreeEmptyState ? <FreeFirstNoFreeResults assetType={assetType} /> : null}
        {isSectionVisible(premium) ? <Section assetType={assetType} fullWidthShowMore={true} section={premium} title={resolvedPremiumTitle} tier={'premium'} /> : null}
      </div>;
});
FreeFirstTrackSections.displayName = 'FreeFirstTrackSections';